import React, { useState, useEffect, useRef } from "react";
import Button from "../../../components/controls/button/Button";
import Draggable from "../../../components/draggable/Draggable";
import styles from "./CustomModal.module.scss";
import { ModalCloseIcon } from "../../../modules/default-svg";
import { createPortal } from "react-dom";
import { FluentProvider, teamsLightTheme, Tooltip } from "@fluentui/react-components";
import MinimalistToggle from "../toggle/MinimalistToggle";
import { Maximize, Minimize } from "lucide-react";

const RESIZE_MIN_WIDTH = 480;
const RESIZE_MIN_HEIGHT = 320;
const RESIZE_VIEWPORT_MARGIN = 32;

const Modal = ({
  title,
  htmlTitle = null,
  handleCancel,
  handleSave,
  body,
  isLoading,
  size,
  saveBtnLabel,
  isSaveBtnDisabled,
  cancelBtnLabel,
  type,
  calendarModalWrap,
  footer = true,
  draggable = true,
  enableFullScreen = false,
  resizable = false,
  zIndex = false,
  width = null,
  additionalFooterButtons = [],
  saveBtnClass,
  checkboxOptions = null, // {id, text, defaultChecked?}
  onConfirmWithOptions = null, // callback for checkbox selections
  // Per-instance style escape hatches (e.g. for the File Preview modal, which wants its body to
  // fill all available height) - kept as plain inline style overrides so no caller's need ever
  // requires touching the shared CustomModal.module.scss rules every other modal relies on.
  contentStyle = null,
  bodyWrapperStyle = null,
  bodyContentStyle = null,
}) => {
  const [mounted, setMounted] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [isMaximized, setIsMaximized] = useState(false);
  const [resizedSize, setResizedSize] = useState(null);
  const contentRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = contentRef.current.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    // The modal stays centered on its anchor point, so it grows on both sides at once - doubling
    // the pointer's movement keeps the dragged corner under the cursor.
    const handleMove = (moveEvent) => {
      setResizedSize({
        width: clamp(
          start.width + 2 * (moveEvent.clientX - start.x),
          RESIZE_MIN_WIDTH,
          window.innerWidth - RESIZE_VIEWPORT_MARGIN
        ),
        height: clamp(
          start.height + 2 * (moveEvent.clientY - start.y),
          RESIZE_MIN_HEIGHT,
          window.innerHeight - RESIZE_VIEWPORT_MARGIN
        ),
      });
    };
    const handleUp = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  };

  useEffect(() => {
    // Initialize checkbox states
    if (checkboxOptions) {
      const initialState = {};
      checkboxOptions.forEach(option => {
        initialState[option.id] = option.defaultChecked ?? false;
      });
      setSelectedOptions(initialState);
    }
  }, [checkboxOptions]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleCheckboxChange = (optionId) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionId]: !prev[optionId]
    }));
  };

  const handleConfirm = (e) => {
    if (checkboxOptions && onConfirmWithOptions) {
      const selectedIds = Object.entries(selectedOptions)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);
      onConfirmWithOptions(selectedIds, selectedOptions);
    } else {
      handleSave(e);
    }
  };

  const modalContent = (
    <Draggable
      draggable={draggable}
      size={size}
      body={
        <div className={`${styles.modalContainer} ${zIndex && "indexing"}`}>
          <div className={styles.modalWrapper}>
            <div
              ref={contentRef}
              className={`${styles.modalContent} ${calendarModalWrap && styles.calendarModalWrap
                }  ${size !== undefined
                  ? type
                    ? styles.ExtraLargeModal
                    : size === "large"
                      ? styles.largeModal
                      : styles.smallModal
                  : ""
                }`}
              style={{
                maxHeight:
                  size === "large" &&
                  (window.innerHeight <= 1000
                    ? "calc(100vh - 100px)"
                    : "900px"),
                height: "auto",
                minHeight: isMaximized ? "calc(100vh - 50px)" : null,
                minWidth: isMaximized ? "calc(100vw - 100px)" : width || null,
                ...(resizedSize && {
                  width: resizedSize.width,
                  maxWidth: resizedSize.width,
                  height: resizedSize.height,
                  maxHeight: resizedSize.height,
                }),
                ...contentStyle,
              }}
            >
              {/* small Large Modal Markup--------------  */}
              <>
                {size !== undefined ? (
                  <>
                    <div className={styles.modalHeader}>
                      <div className="d-flex justify-content-between align-items-center">
                        {
                          htmlTitle ?
                            htmlTitle :
                            <h4>{title}</h4>
                        }
                        <div className="d-flex align-items-center">
                          {enableFullScreen && (
                            <Tooltip content={isMaximized ? "Restore" : "Maximize"} relationship="label">
                              <span
                                onClick={() => setIsMaximized(!isMaximized)}
                                className="d-inline-flex cursor-pointer me-2"
                                title={isMaximized ? "Restore" : "Maximize"}
                              >
                                {isMaximized ? <Minimize size={18} color="#342C82" /> : <Maximize size={18} color="#342C82" />}
                              </span>
                            </Tooltip>

                          )}
                          <span
                            onClick={handleCancel}
                            className="d-inline-flex cursor-pointer"
                          >
                            <ModalCloseIcon />
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      className={styles.modalbodyWrapper}
                      style={resizable ? { flex: "1 1 auto", minHeight: 0, maxHeight: "none", ...bodyWrapperStyle } : bodyWrapperStyle}
                    >
                      <div className={styles.modalbodyContent} style={bodyContentStyle}>{body}</div>
                    </div>
                    {footer && (
                      <div className={styles.modalFooter}>
                        <div className={styles.btnContainer}>
                          <Button
                            label={cancelBtnLabel}
                            btnClass="d_btn d_btn_whiteMd me-10"
                            btnType="secondary"
                            clickHandler={handleCancel}
                            isDisabled={isLoading}
                          />
                          <Button
                            label={saveBtnLabel}
                            btnClass="d_btn d_btn_primaryMd"
                            clickHandler={handleSave}
                            showLoading={isLoading}
                            isDisabled={isSaveBtnDisabled || isLoading}
                          />
                          {additionalFooterButtons.map((button, index) => (
                            <Button
                              key={index}
                              label={button.label}
                              btnClass={button.btnClass || "d_btn d_btn_primary me-10"}
                              btnType={button.btnType || "primary"}
                              clickHandler={button.onClick}
                              isDisabled={button.isDisabled || isLoading}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {resizable && (
                      <div className={styles.resizeBar}>
                        <span className={styles.resizeHandle} title="Drag to resize" onMouseDown={startResize}>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <path d="M13 5L5 13M13 9L9 13" />
                          </svg>
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  /* confirmation Modal Markup-------------- */
                  <div className={styles.confirmationModal}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h4>{title}</h4>
                      <span
                        onClick={handleCancel}
                        className="d-inline-flex cursor-pointer"
                      >
                        <ModalCloseIcon />
                      </span>
                    </div>
                    <div className={styles.confimationmodalbody}>
                      {body}
                      {checkboxOptions && checkboxOptions.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {checkboxOptions.map((option) => (
                            <div key={option.id} className="flex items-center gap-2">
                              <MinimalistToggle
                                title={option.text}
                                checked={selectedOptions[option.id] || false}
                                onChange={() => handleCheckboxChange(option.id)}
                                disabled={option.isDisabled || isLoading}
                                titlePosition="right"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {footer && (
                      <div className={styles.btnContainer}>
                        {additionalFooterButtons.map((button, index) => (
                          <Button
                            key={index}
                            label={button.label}
                            btnClass={button.btnClass || "d_btn d_btn_primary me-10"}
                            btnType={button.btnType || "primary"}
                            clickHandler={button.onClick}
                            isDisabled={button.isDisabled || isLoading}
                          />
                        ))}
                        <Button
                          label={saveBtnLabel}
                          btnClass={`d_btn d_btn_primary me-10 ${saveBtnClass}`}
                          clickHandler={handleConfirm}
                          showLoading={isLoading}
                          isDisabled={isSaveBtnDisabled || isLoading}
                        />
                        <Button
                          label={cancelBtnLabel}
                          btnClass="d_btn d_btn_smallWhite"
                          btnType="secondary"
                          clickHandler={handleCancel}
                          isDisabled={isLoading}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
      }
    />
  );

  return mounted
    ? createPortal(
      <FluentProvider theme={teamsLightTheme}>{modalContent}</FluentProvider>,
      document.body
    )
    : null;
};

export default Modal;
